import React, { useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Text,
  Textarea,
  VStack,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { FaPaperPlane, FaTimes, FaComments } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { supportAPI } from '../services/api';

const DEFAULT_ASUNTO = 'Sugerencia / Duda / Problema';

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

export default function SupportWidget() {
  
  const { user, profile, isAuthenticated } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [email, setEmail] = useState('');
  const [asunto, setAsunto] = useState(DEFAULT_ASUNTO);
  const [mensaje, setMensaje] = useState('');

  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.600', 'gray.300');

  const initialEmail = useMemo(() => {
    return String(profile?.email || user?.email || '').trim();
  }, [profile?.email, user?.email]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!email && initialEmail) setEmail(initialEmail);
  }, [isAuthenticated, initialEmail, email]);

  const canSend =
    isValidEmail(email) && String(asunto || '').trim().length >= 3 && String(mensaje || '').trim().length >= 10;

  const handleSend = async () => {
    if (!canSend || isSending) return;
    try {
      setIsSending(true);
      await supportAPI.createTicket({
        email: String(email || '').trim(),
        asunto: String(asunto || '').trim(),
        mensaje: String(mensaje || '').trim(),
      });
      toaster.create({
        title: 'Enviado a soporte',
        description: 'Gracias. Hemos recibido tu mensaje.',
        type: 'success',
        duration: 4000,
        closable: true,
        position: 'top-right',
      });
      setMensaje('');
      setAsunto(DEFAULT_ASUNTO);
      setIsOpen(false);
    } catch (e) {
      console.error('Error enviando soporte:', e);
      toaster.create({
        title: 'No se pudo enviar',
        description: 'Inténtalo de nuevo en unos segundos.',
        type: 'error',
        duration: 5000,
        closable: true,
        position: 'top-right',
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Box position="fixed" right={{ base: 4, md: 6 }} bottom={{ base: 4, md: 6 }} zIndex={2000}>
      {isOpen ? (
        <Box
          w={{ base: 'calc(100vw - 32px)', sm: '360px' }}
          maxW="360px"
          bg={bg}
          borderWidth="1px"
          borderColor={border}
          rounded="2xl"
          shadow="xl"
          overflow="hidden"
        >
          <HStack justify="space-between" px={4} py={3} bg={useColorModeValue('purple.50', 'gray.900')}>
            <HStack gap={2}>
              <Icon color="purple.500" asChild><FaComments /></Icon>
              <Text fontWeight="800">Soporte</Text>
            </HStack>
            <IconButton
              aria-label="Cerrar soporte"
              size="sm"
              variant="ghost"
              onClick={() => setIsOpen(false)}><Icon asChild><FaTimes /></Icon></IconButton>
          </HStack>

          <VStack align="stretch" gap={3} p={4}>
            <Text fontSize="sm" color={muted}>
              Envíanos tu sugerencia, duda o problema y lo revisamos lo antes posible.
            </Text>

            <Field.Root required>
              <Field.Label fontSize="sm">Email</Field.Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                type="email"
              />
            </Field.Root>

            <Field.Root required>
              <Field.Label fontSize="sm">Asunto</Field.Label>
              <Input value={asunto} onChange={(e) => setAsunto(e.target.value)} placeholder="Asunto" />
            </Field.Root>

            <Field.Root required>
              <Field.Label fontSize="sm">Mensaje</Field.Label>
              <Textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Describe el problema o tu sugerencia…"
                rows={5}
                resize="none"
              />
              <Text mt={1} fontSize="xs" color={muted}>
                Mínimo 10 caracteres.
              </Text>
            </Field.Root>

            <Button
              colorPalette="purple"
              onClick={handleSend}
              disabled={!canSend}
              loading={isSending}
              loadingText="Enviando…"><Icon asChild><FaPaperPlane /></Icon>Enviar
                          </Button>
          </VStack>
        </Box>
      ) : (
        <IconButton
          aria-label="Abrir soporte"
          colorPalette="purple"
          rounded="full"
          size="lg"
          shadow="xl"
          onClick={() => setIsOpen(true)}><Icon asChild><FaComments /></Icon></IconButton>
      )}
    </Box>
  );
}

